import { forwardRef, useMemo } from "react";
import { MindMap, PALETTE } from "@/components/MindMap";
import { fmtDate } from "@/lib/api";

export const SHEET_W = 1600;
export const SHEET_H = 1131;
const MAP_SCALE = 0.86;

const Label = ({ children, color = "#0D9488" }) => (
  <p className="font-mono text-[11px] font-semibold uppercase tracking-widest mb-2" style={{ color }}>{children}</p>
);

const fallbackFacts = (m) =>
  (m?.branches || []).slice(0, 4).map((b) => ({ label: b.keyword || b.title, text: b.children?.[0]?.text || "", emoji: b.emoji }));

const fallbackCards = (m) =>
  (m?.branches || []).slice(0, 4).map((b) => ({ q: `Cosa sai di “${b.title}”?`, a: (b.children || []).map((c) => c.text).slice(0, 2).join(" · ") }));

export const StudySheet = forwardRef(({ work, still = true }, ref) => {
  const mm = work.mindmap;
  const m = useMemo(() => mm || {}, [mm]);
  const facts = useMemo(() => (m.key_facts?.length ? m.key_facts : fallbackFacts(m)), [m]);
  const cards = useMemo(() => (m.flashcards?.length ? m.flashcards : fallbackCards(m)), [m]);
  return (
    <div ref={ref} className="bg-white text-zinc-950 flex flex-col" style={{ width: SHEET_W, height: SHEET_H, padding: 28, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
      <header className="flex items-end justify-between border-b-2 border-zinc-950 pb-3">
        <div className="flex items-center gap-3">
          <span className="text-4xl leading-none">{m.emoji}</span>
          <div>
            <p className="font-mono text-[11px] uppercase tracking-widest text-[#0D9488]">Scheda di studio · Memoria visiva</p>
            <h1 className="font-display text-[30px] font-extrabold tracking-tight leading-none mt-1">{work.title || m.title}</h1>
          </div>
        </div>
        <div className="text-right font-mono text-[11px] uppercase tracking-widest text-zinc-500 leading-relaxed">
          <div className="flex items-center justify-end gap-2.5 mb-1"><img src={`${process.env.PUBLIC_URL}/logo-app.png`} alt="Aurora School" data-testid="sheet-logo" style={{ width: 46, height: 46, borderRadius: 10 }} /><p className="font-display text-[18px] font-extrabold tracking-tight normal-case text-zinc-950">Aurora School</p></div>
          <p>{[work.author, work.classe, fmtDate(work.created_at || new Date())].filter(Boolean).join(" · ")}</p>
        </div>
      </header>

      <div className="flex gap-5 mt-4" style={{ height: 860 * MAP_SCALE }}>
        <div className="border border-zinc-200 overflow-hidden shrink-0" style={{ width: 1200 * MAP_SCALE, height: 860 * MAP_SCALE }}>
          <div style={{ transform: `scale(${MAP_SCALE})`, transformOrigin: "top left" }}>
            <MindMap map={m} still={still} framed={false} />
          </div>
        </div>
        <div className="flex-1 flex flex-col gap-4 min-w-0">
          <section className="bg-zinc-950 text-white p-4">
            <Label color="#5EEAD4">Riassunto lampo</Label>
            <p className="text-[13.5px] leading-snug">{m.summary}</p>
          </section>
          <section className="flex-1 flex flex-col">
            <Label>Da ricordare</Label>
            <div className="grid grid-cols-1 gap-2 flex-1">
              {facts.map((f, i) => {
                const col = PALETTE[i % PALETTE.length];
                return (
                  <div key={`${f.label}-${i}`} className="flex items-start gap-3 p-3" style={{ background: col.bg, borderLeft: `4px solid ${col.c}` }}>
                    <span className="text-2xl leading-none">{f.emoji}</span>
                    <div className="min-w-0">
                      <p className="font-display font-extrabold text-[15px] leading-tight" style={{ color: col.c }}>{f.label}</p>
                      <p className="text-[12.5px] leading-snug text-zinc-700 mt-0.5">{f.text}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      <section className="mt-4 flex-1 flex flex-col min-h-0">
        <Label>Flashcard di ripasso</Label>
        <div className="grid grid-cols-4 gap-3 flex-1">
          {cards.map((c, i) => {
            const col = PALETTE[i % PALETTE.length];
            return (
              <div key={`${c.q}-${i}`} className="border border-zinc-200 flex flex-col">
                <div className="px-3 py-2 flex gap-2 items-start" style={{ background: col.c }}>
                  <span className="font-mono text-[11px] font-bold text-white/80">D{i + 1}</span>
                  <p className="text-[13px] font-semibold text-white leading-snug">{c.q}</p>
                </div>
                <p className="px-3 py-2 text-[12.5px] leading-snug text-zinc-700 flex-1"><span className="font-mono text-[11px] font-bold mr-1" style={{ color: col.c }}>R</span>{c.a}</p>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
});
StudySheet.displayName = "StudySheet";
